import { z } from 'zod'

import { LocaleSchema } from './common'
import { SecretPatchSchema, SecretStatusSchema } from './tracking'

// AI writing assistant (package flag `ai_writing`). The site owner pastes their own Anthropic API key in Settings, so
// each site pays for its own tokens. The key is stored AES-GCM sealed (like the tracking secrets) and never returned.
// Every request is capped: input size, output tokens, requests per user per minute and per site per day.

/** Claude Haiku 4.5: fast and cheap enough for short website copy. */
export const AI_WRITING_MODEL = 'claude-haiku-4-5-20251001'

export const AI_WRITING_LIMITS = {
  /** Longest text sent to be improved, shortened, expanded or translated (characters). */
  inputChars: 12_000,
  /** Longest brief for a new draft (characters). */
  briefChars: 2_000,
  /** Output cap per request (Anthropic `max_tokens`). */
  maxTokens: 1_500,
  /** Requests per site per day (UTC). Counted from the usage table, failed calls included. */
  dailyRequests: 200,
  /** Requests per user per minute. */
  userRequestsPerMinute: 10,
  /** Give up on Anthropic after this long. */
  timeoutMs: 30_000,
  /** Usage rows older than this are deleted as new ones are written. */
  keepDays: 400,
} as const

export const AI_ACTIONS = ['draft', 'improve', 'shorten', 'expand', 'translate'] as const
export const AiActionSchema = z.enum(AI_ACTIONS)
export type AiAction = z.infer<typeof AiActionSchema>

export const AI_TONES = ['formal', 'casual'] as const
export const AiToneSchema = z.enum(AI_TONES)
export type AiTone = z.infer<typeof AiToneSchema>

/** `plain`: a text box (no formatting). `rich`: a rich-text editor (paragraphs, `## ` headings, `- ` bullets). */
export const AiFormatSchema = z.enum(['plain', 'rich'])
export type AiFormat = z.infer<typeof AiFormatSchema>

/** Content areas that may use the assistant; the user needs `<area>:edit`. */
export const AiAreaSchema = z.enum(['pages', 'articles', 'catalogue'])
export type AiArea = z.infer<typeof AiAreaSchema>

export const AiGenerateRequestSchema = z
  .object({
    area: AiAreaSchema,
    action: AiActionSchema,
    tone: AiToneSchema.default('formal'),
    format: AiFormatSchema.default('plain'),
    /** Language of the result (for `translate`: the target language). */
    locale: LocaleSchema,
    /** `translate` only: the language `text` is in. */
    sourceLocale: LocaleSchema.optional(),
    /** The current text of the field (required for every action but `draft`). */
    text: z.string().max(AI_WRITING_LIMITS.inputChars).default(''),
    /** `draft`: what to write about. */
    brief: z.string().max(AI_WRITING_LIMITS.briefChars).default(''),
    /** Context: the title of the page / article / product being edited. */
    title: z.string().max(300).default(''),
    /** Context: which field this is, e.g. "Summary" or "Description". */
    field: z.string().max(100).default(''),
  })
  .superRefine((r, ctx) => {
    if (r.action === 'draft' && !r.brief.trim() && !r.title.trim()) ctx.addIssue({ code: 'custom', path: ['brief'], message: 'Describe what to write' })
    if (r.action !== 'draft' && !r.text.trim()) ctx.addIssue({ code: 'custom', path: ['text'], message: 'There is no text to work on' })
    if (r.action === 'translate' && (!r.sourceLocale || r.sourceLocale === r.locale)) ctx.addIssue({ code: 'custom', path: ['sourceLocale'], message: 'Pick another language' })
  })
export type AiGenerateRequest = z.input<typeof AiGenerateRequestSchema>

export const AiGenerateResponseSchema = z.object({
  text: z.string(),
  /** The answer hit the output cap and may end mid-sentence. */
  truncated: z.boolean(),
  usage: z.object({ inputTokens: z.number().int(), outputTokens: z.number().int() }),
  /** Requests left for the site today. */
  remainingToday: z.number().int(),
})
export type AiGenerateResponse = z.infer<typeof AiGenerateResponseSchema>

/** What every editor may read: is the assistant usable here, without revealing anything about the key. */
export const AiStatusSchema = z.object({
  /** The plan includes `ai_writing`. */
  enabled: z.boolean(),
  /** An API key is set. */
  configured: z.boolean(),
  dailyLimit: z.number().int(),
  remainingToday: z.number().int(),
})
export type AiStatus = z.infer<typeof AiStatusSchema>

const UsageTotalsSchema = z.object({ requests: z.number().int(), inputTokens: z.number().int(), outputTokens: z.number().int() })

/** GET /api/admin/ai/settings (settings:view): key status (never the key) and consumption. */
export const AiSettingsViewSchema = z.object({
  enabled: z.boolean(),
  model: z.string(),
  apiKey: SecretStatusSchema,
  usage: z.object({ month: UsageTotalsSchema, today: UsageTotalsSchema }),
  limits: z.object({ dailyRequests: z.number().int(), userRequestsPerMinute: z.number().int(), maxTokens: z.number().int(), inputChars: z.number().int() }),
})
export type AiSettingsView = z.infer<typeof AiSettingsViewSchema>

/** PUT /api/admin/ai/settings: keep, replace or remove the key. Anthropic keys start with `sk-ant-`. */
export const AiSettingsUpdateSchema = z.object({
  apiKey: SecretPatchSchema.refine((p) => p.action !== 'set' || /^sk-ant-[A-Za-z0-9_-]{20,}$/.test(p.value), { message: 'Looks like sk-ant-…' }),
})
export type AiSettingsUpdate = z.infer<typeof AiSettingsUpdateSchema>
